import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter } from 'lucide-react';
import { 
  categories, 
  levels, 
  filterFormations 
} from './data/products';

const Catalog = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeLevel, setActiveLevel] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Filtrer les formations en fonction des critères
  const filteredFormations = filterFormations({
    category: activeCategory,
    level: activeLevel,
    searchTerm: searchQuery
  });

  return (
    <div className="min-h-screen text-white pt-20 md:pt-32">
      {/* Hero Section - Responsive */}
      <div className="container mx-auto px-4 mb-8 md:mb-16">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl md:text-5xl font-bold mb-4 md:mb-6">
            Catalogue de
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#017D7E] to-[#00A99D]">
              Formations
            </span>
          </h1>
          <p className="text-base md:text-xl text-white/70">
            Développez vos compétences avec nos formations expertes
          </p>
        </div>
      </div>

      {/* Search and Filters - Responsive */}
      <div className="container mx-auto px-4 mb-8 md:mb-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row gap-4 mb-6 md:mb-8">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="text"
                placeholder="Rechercher une formation..."
                className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm md:text-base"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <button 
              className="md:w-auto px-6 py-3 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center gap-2"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            >
              <Filter className="w-5 h-5" />
              <span className="text-sm md:text-base">Filtres</span>
            </button>
          </div>

          <div className={`space-y-6 ${isFilterOpen ? 'block' : 'hidden md:block'}`}>
            <div className="flex flex-wrap gap-3 md:gap-4 mb-6 md:mb-8">
              <CategoryButton
                active={activeCategory === 'all'}
                onClick={() => setActiveCategory('all')}
                label="Toutes les catégories"
              />
              {categories.map(category => (
                <CategoryButton
                  key={category.id}
                  active={activeCategory === category.id}
                  onClick={() => setActiveCategory(category.id)}
                  label={category.name}
                  icon={category.icon}
                />
              ))}
            </div>

            <div className="flex flex-wrap gap-3 md:gap-4">
              <LevelButton
                active={activeLevel === 'all'}
                onClick={() => setActiveLevel('all')}
                label="Tous les niveaux"
              />
              {levels.map(level => (
                <LevelButton
                  key={level.id}
                  active={activeLevel === level.id}
                  onClick={() => setActiveLevel(level.id)}
                  label={level.name}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Courses Grid - Responsive */}
      <div className="container mx-auto px-4 mb-8 md:mb-16">
        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-8">
          {filteredFormations.length > 0 ? (
            filteredFormations.map(formation => (
              <CourseCard
                key={formation.id}
                course={formation}
                onNavigate={() => navigate(`/academy/course/${formation.id}`)}
              />
            ))
          ) : (
            <div className="col-span-1 sm:col-span-2 md:col-span-3 text-center text-white/70 py-8 md:py-12">
              Aucune formation ne correspond à vos critères.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CategoryButton = ({ active, onClick, label, icon: Icon }) => (
  <button
    className={`px-4 md:px-6 py-2 md:py-3 rounded-xl border flex items-center gap-2 text-sm md:text-base ${
      active
        ? 'border-green-500 bg-[#017D7E]/10 text-[#017D7E]'
        : 'border-white/10 hover:border-green-500/50'
    }`}
    onClick={onClick}
  >
    {Icon && <Icon className="w-4 h-4 md:w-5 md:h-5" />}
    {label}
  </button>
);

const LevelButton = ({ active, onClick, label }) => (
  <button
    className={`px-4 md:px-6 py-2 rounded-full text-sm md:text-base ${
      active
        ? 'bg-[#017D7E]'
        : 'bg-white/10 hover:bg-white/20'
    }`}
    onClick={onClick}
  >
    {label}
  </button>
);

const CourseCard = ({ course, onNavigate }) => (
  <div
    className="bg-white/5 rounded-xl border border-white/10 hover:border-green-500/50 transition-all cursor-pointer overflow-hidden"
    onClick={onNavigate}
  >
    {course.image && (
      <img
        src={course.image}
        alt={course.title}
        className="w-full h-36 md:h-48 object-cover"
      />
    )}
    <div className="p-4 md:p-6">
      <div className="flex flex-wrap items-center gap-2 text-xs md:text-sm text-[#017D7E] mb-2">
        <span className="px-2 py-1 rounded-full bg-[#017D7E]/10">
          {course.category}
        </span>
        <span className="px-2 py-1 rounded-full bg-[#017D7E]/10">
          {course.level}
        </span>
      </div>
      <h3 className="text-lg md:text-xl font-bold mb-2">{course.title}</h3>
      <div className="flex items-center gap-4 text-white/70 text-xs md:text-sm">
        <span className="flex items-center gap-1">
          {course.duration}
        </span>
        {course.students && (
          <span className="flex items-center gap-1">
            {course.students} élèves
          </span>
        )}
      </div>
    </div>
  </div>
);

export default Catalog;